import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogContent, MatDialogRef } from "@angular/material/dialog";
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatSelectModule } from "@angular/material/select";
import { MatOptionModule } from "@angular/material/core";
import { NgIf } from "@angular/common";

import { DialogData } from "@core/interfaces/dialog-data";
import { GEMPService} from "@core/http/table-data-services/GEMP.service";
import { GEMP } from "@core/models/GEMP";
import { UpsertGempPayload } from "@core/models/Http/Payloads/UpsertGempPayload";
import { FileUploadComponent } from "@shared/components/file-upload/file-upload.component";
import { REGEX } from "@shared/utils/regex.constants";

type GempForm = FormGroup<{
  idGEMP?: FormControl<string>;   // opcional (solo en edicion)
  nombre: FormControl<string>;
  logoBase64: FormControl<string>; // ← en GEMP guardamos SOLO base64 puro
  abreviatura: FormControl<string>;
}>;

@Component({
  selector: 'app-groups-create-edit',
  standalone: true,
  imports: [
    // Angular
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    // Material
    MatDialogContent,
    MatDialogClose,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatDatepickerModule,
    MatSelectModule,
    MatOptionModule,
    // Shared
    FileUploadComponent,
  ],
  templateUrl: './groups-create-edit.component.html',
  styleUrl: './groups-create-edit.component.scss'
})
export class GroupsCreateEditComponent {
  action: 'new' | 'edit';
  dialogTitle: string;
  groupsForm: GempForm;
  gemp: GEMP;

  constructor(
    public dialogRef: MatDialogRef<GroupsCreateEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData<GEMP>,
    private readonly groupsService: GEMPService,
    private readonly fb: FormBuilder,
  ) {
    this.action = (data.action === 'edit' ? 'edit' : 'new');
    if (this.action === 'edit') {
      this.dialogTitle = 'EDITAR: ' + data.entity.nombre;
      this.gemp = data.entity;
    } else {
      this.dialogTitle = 'NUEVO GRUPO EMPRESARIAL';
      this.gemp = new GEMP({} as GEMP);
    }
    this.groupsForm = this.createForm();
  }

  private createForm(): GempForm {
    const form = this.fb.group({
      nombre: this.fb.control(this.gemp.nombre ?? '', {
        validators: [Validators.required],
        nonNullable: true,
      }),
      // En GEMP guardamos SOLO base64 puro
      logoBase64: this.fb.control<string>(this.gemp.logoBase64 ?? '', {
        validators: [Validators.required],
        nonNullable: true,
      }),
      abreviatura: this.fb.control(this.gemp.abreviatura ?? '', {
        validators: [
          Validators.required,
          Validators.pattern(REGEX.gemp_abrev),
          Validators.maxLength(3),
        ],
        nonNullable: true,
      }),
    });

    // Solo en edicion agrega idGEMP
    if (this.action === 'edit' && this.gemp.idGEMP) {
      (form as unknown as FormGroup).addControl(
        'idGEMP',
        this.fb.control(this.gemp.idGEMP)
      );
    }

    return form as GempForm;
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  confirmAdd(): void {
    if (this.groupsForm.invalid) {
      this.groupsForm.markAllAsTouched();
      return;
    }

    // Al usar emitBase64Only en el uploader, aqui ya tenemos SOLO base64
    const plainBase64 = this.groupsForm.get('logoBase64')!.value || '';

    if (!plainBase64) {
      this.groupsForm.get('logoBase64')?.setErrors({ required: true });
      return;
    }

    const payload: UpsertGempPayload = {
      ...this.groupsForm.getRawValue(),
      logoBase64: plainBase64,
    };

    this.groupsService.save(payload);
    this.dialogRef.close(true);
  }
}
